/**
 * graft/A5: the Search agent dock — a floating bottom-right launcher that
 * opens a minimizable multi-turn agent card, available on EVERY page.
 *
 * Architecture (the OmniSearch-v3 pattern on the production stack): the
 * agent loop is hosted by the search service's relay (/api/v1/agent/* —
 * a Claude Managed Agent behind the same JWT auth as search); THIS
 * component executes the agent's tools in the browser against the same
 * endpoints the search box uses:
 *   search(query)  -> POST {searchBaseUrl}v1/search/evaluate  (the RRF
 *                     ranked fast path, same auth as the search box)
 *   get_record(rN) -> GET {searchBaseUrl}v1/record/... (entity_lookup +
 *                     expand_related — the graph reads)
 *
 * PAGE CONTEXT: every user turn is prefixed (server-side prompt, not
 * shown in the UI) with a compact snapshot of where the user is — URL
 * path, document title, breadcrumb/heading text — so "what am I looking
 * at?" and "how do I use this page?" work. The snapshot is captured at
 * ask-time from the live DOM: path + title + the first h1/h2/breadcrumb
 * text + any data-testid page markers, truncated hard at 1.2KB.
 *
 * GATING: renders when the AUTHED search surface answers — probed via
 * GET {searchBaseUrl}v1/search/suggest (200/401 = the route exists, so
 * the relay stack is deployed; 404/HTML = absent, dock stays hidden).
 * The relay shipped as real authed endpoints (/api/v1/agent/*,
 * /v1/record/*) so hosted environments no longer need the dev surface.
 * The dev builder/user SWITCHER stays gated on the dev-surface probe in
 * devContext.ts — that surface is deliberately not promoted (P1a).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { type DevContext, devContextParams, loadDevContext } from "./devContext";

interface ChatItem {
    role: "user" | "agent" | "activity";
    text: string;
}

interface HandleRec {
    entity: string;
    id: string;
    summary?: string;
}

interface AgentToolCall {
    id: string;
    name: string;
    input: { query?: string; id?: string };
}

interface RelayResponse {
    text?: string;
    tools?: AgentToolCall[];
    done?: boolean;
    seen?: string[];
    session_id?: string;
}

/** Compact where-am-I snapshot for the agent's context.
 *
 * Selector stability (review 1529278): path/title/h1/h2/[role='tab'] are
 * stable APIs; the class-substring selectors (pageTitle/breadcrumb) are
 * best-effort against CSS-module names and WILL quietly stop matching if
 * those components are renamed. That degradation is ACCEPTED for this
 * dev-only surface: every selector is additive context — the agent still
 * gets path+title+headings when they miss. Do not copy this pattern into
 * production code; a product version needs a real page-context contract. */
function capturePageContext(): string {
    const parts: string[] = [];
    parts.push(`path: ${window.location.pathname}${window.location.hash || ""}`);
    if (document.title) parts.push(`title: ${document.title}`);
    const heading = document.querySelector("h1, h2, [class*='pageTitle'], [class*='PageTitle']");
    if (heading?.textContent?.trim())
        parts.push(`heading: ${heading.textContent.trim().slice(0, 120)}`);
    const crumbs = Array.from(
        document.querySelectorAll("[class*='breadcrumb'] a, [class*='Breadcrumb'] a"),
    )
        .map((a) => a.textContent?.trim())
        .filter(Boolean)
        .slice(0, 5);
    if (crumbs.length) parts.push(`breadcrumbs: ${crumbs.join(" > ")}`);
    // Visible tab/section labels help "how does this page work" questions.
    const tabs = Array.from(document.querySelectorAll("[role='tab']"))
        .map((t) => t.textContent?.trim())
        .filter(Boolean)
        .slice(0, 8);
    if (tabs.length) parts.push(`tabs: ${tabs.join(", ")}`);
    return parts.join("\n").slice(0, 1200);
}

export function SearchAgentDock(props: { searchBaseUrl: string; getToken: () => Promise<string> }) {
    const { searchBaseUrl, getToken } = props;
    const [available, setAvailable] = useState(false);
    const [minimized, setMinimized] = useState(true);
    const [chat, setChat] = useState<ChatItem[]>([]);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const sessionRef = useRef<string | null>(null);
    const seenRef = useRef<string[]>([]);
    const handlesRef = useRef<Record<string, HandleRec>>({});
    const handleSeq = useRef(0);
    const threadEndRef = useRef<HTMLDivElement | null>(null);

    // Availability probe: the dock exists only where the AUTHED surface
    // answers. GET v1/search/suggest responds 200 (valid token) or 401
    // (route mounted, token rejected/absent) wherever the promoted stack
    // is deployed; a 404 — or an SPA catch-all serving HTML with a 200 —
    // means the surface is absent and the dock stays hidden. The
    // content-type check is load-bearing: an SPA catch-all masquerades as
    // any GET endpoint with an HTML 200 (see the ephpr round-3 capture),
    // so `ok` alone is not proof the route exists.
    // (Second half of the DOUBLE gate — MainNavSearchAgentDock already
    // requires isProduction === false before mounting this at all.
    // Deliberately NOT setDevSurfaceConfirmed: that gate belongs to the
    // dev-only switcher surface, which this probe says nothing about.)
    useEffect(() => {
        const controller = new AbortController();
        void (async () => {
            try {
                const token = await getToken().catch(() => "");
                const res = await fetch(`${searchBaseUrl}v1/search/suggest?q=probe`, {
                    signal: controller.signal,
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
                const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
                const surfaceExists = (res.ok && isJson) || res.status === 401;
                if (!controller.signal.aborted && surfaceExists) {
                    setAvailable(true);
                }
            } catch {
                /* unreachable service: dock stays hidden */
            }
        })();
        return () => controller.abort();
    }, [searchBaseUrl, getToken]);

    // v3 triage handoff: the search panel's AgentTriageBanner dispatches
    // bt:openSearchAgent with the committed query — open the dock and ask
    // in the same conversation (the banner is the only writer; the event
    // never fires in deployed envs because the banner is probe-gated).
    const askRef = useRef<(q: string) => Promise<void>>();
    useEffect(() => {
        const onSeed = (e: Event) => {
            const q = (e as CustomEvent<{ query?: string }>).detail?.query;
            if (typeof q === "string" && q.trim()) {
                setMinimized(false);
                void askRef.current?.(q);
            }
        };
        window.addEventListener("bt:openSearchAgent", onSeed);
        return () => window.removeEventListener("bt:openSearchAgent", onSeed);
    }, []);

    // Abort plumbing for the relay pump (review 1529277): minimize/unmount
    // mid-turn must stop the loop instead of leaving it issuing searches and
    // relay round-trips against an unmounted component.
    const pumpAbortRef = useRef<AbortController | null>(null);
    useEffect(
        () => () => {
            pumpAbortRef.current?.abort();
        },
        [],
    );

    const push = useCallback((item: ChatItem) => setChat((prev) => [...prev, item]), []);

    const execSearch = useCallback(
        async (query: string): Promise<string> => {
            // Tool calls take the /evaluate fast path, not the SSE stream:
            // the stream stays open through LLM intent analysis + QA (~7-10s)
            // while the ranked hits the agent needs are ready in ~1s.
            // /evaluate with the non-LLM stages returns the RRF-FUSED ranked
            // list in one JSON body — faster AND better-ordered than the
            // stream's arrival-order hit dedupe. (Profiled 2026-08-26: tool
            // execution was 66% of a 25s agent ask; this cuts it to ~1s.)
            const ctx: DevContext = loadDevContext();
            const token = await getToken().catch(() => "");
            const res = await fetch(`${searchBaseUrl}v1/search/evaluate${devContextParams(ctx)}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ query, stages: ["resolver", "keyword", "semantic"] }),
            });
            if (!res.ok) return `search failed (${res.status})`;
            const data = await res.json();
            const ranked: Array<{ entity: string; id: string | number; summary?: string }> =
                Array.isArray(data?.ranked) ? data.ranked : [];
            const lines: string[] = [];
            for (const h of ranked.slice(0, 8)) {
                const handle = `r${++handleSeq.current}`;
                handlesRef.current[handle] = {
                    entity: h.entity,
                    id: String(h.id),
                    summary: h.summary,
                };
                lines.push(`${handle} [${h.entity}] ${(h.summary || "(no title)").slice(0, 120)}`);
            }
            return lines.length ? lines.join("\n") : "no results";
        },
        [searchBaseUrl, getToken],
    );

    const execGetRecord = useCallback(
        async (handle: string): Promise<string> => {
            const rec = handlesRef.current[handle.trim()];
            if (!rec) return `unknown handle ${handle}`;
            // devContextParams kept: inert on authed routes (server takes
            // tenancy from the JWT), still honored under AUTH_DISABLED local.
            const ctx = loadDevContext();
            const token = await getToken().catch(() => "");
            const res = await fetch(
                `${searchBaseUrl}v1/record/${encodeURIComponent(rec.entity)}/${encodeURIComponent(rec.id)}${devContextParams(ctx)}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                },
            );
            if (!res.ok) return `get_record failed (${res.status})`;
            const data = await res.json();
            const lines: string[] = [`${rec.entity} #${rec.id}: ${rec.summary || ""}`];
            const fields = data?.record?.fields;
            if (fields) {
                for (const [k, v] of Object.entries(fields).slice(0, 12)) {
                    if (v != null && String(v).length < 200) lines.push(`  ${k}: ${v}`);
                }
            }
            const related = data?.related?.related;
            if (Array.isArray(related) && related.length) {
                lines.push("RELATED:");
                for (const r of related.slice(0, 15)) {
                    const h2 = `r${++handleSeq.current}`;
                    handlesRef.current[h2] = {
                        entity: r.ref.entity_type,
                        id: r.ref.id,
                        summary: r.summary,
                    };
                    lines.push(
                        `  ${h2} --${r.relation}(${r.direction})--> [${r.ref.entity_type}] ${(r.summary || r.ref.id).slice(0, 100)}`,
                    );
                }
            }
            return lines.join("\n");
        },
        [searchBaseUrl, getToken],
    );

    const pump = useCallback(
        async (first: RelayResponse, signal: AbortSignal) => {
            let resp = first;
            for (let round = 0; round < 12; round++) {
                if (signal.aborted) return; // dock closed/unmounted mid-turn
                if (resp.text?.trim()) push({ role: "agent", text: resp.text.trim() });
                seenRef.current = resp.seen || seenRef.current;
                if (!resp.tools?.length) break;
                const results = [];
                for (const t of resp.tools) {
                    if (signal.aborted) return;
                    const label =
                        t.name === "search"
                            ? `search("${t.input?.query ?? ""}")`
                            : `${t.name}(${t.input?.id ?? ""})`;
                    push({ role: "activity", text: label });
                    let content: string;
                    try {
                        content =
                            t.name === "search"
                                ? await execSearch(String(t.input?.query ?? ""))
                                : t.name === "get_record"
                                  ? await execGetRecord(String(t.input?.id ?? ""))
                                  : `unknown tool ${t.name}`;
                    } catch (e) {
                        content = `tool error: ${e}`;
                    }
                    results.push({ id: t.id, content });
                }
                const token = await getToken().catch(() => "");
                const r = await fetch(`${searchBaseUrl}v1/agent/tools`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        session_id: sessionRef.current,
                        results,
                        seen: seenRef.current,
                    }),
                    signal,
                });
                if (!r.ok) {
                    setError(`relay ${r.status}`);
                    break;
                }
                resp = (await r.json()) as RelayResponse;
            }
        },
        [searchBaseUrl, getToken, execSearch, execGetRecord, push],
    );

    const ask = useCallback(
        async (q: string) => {
            const t = q.trim();
            if (!t || busy) return;
            setError(null);
            setBusy(true);
            push({ role: "user", text: t });
            try {
                const ctx = loadDevContext();
                const token = await getToken().catch(() => "");
                const authHeaders = {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                };
                if (!sessionRef.current) {
                    // builder/user body fields are inert on the authed route
                    // (server takes tenancy from the JWT); still honored
                    // under AUTH_DISABLED local, so keep sending them.
                    const r = await fetch(`${searchBaseUrl}v1/agent/new`, {
                        method: "POST",
                        headers: authHeaders,
                        body: JSON.stringify({
                            title: t.slice(0, 60),
                            builder: ctx.builderId || "jwt",
                            user: ctx.userId || "jwt",
                        }),
                    });
                    if (!r.ok) throw new Error(`agent/new ${r.status}`);
                    sessionRef.current = ((await r.json()) as RelayResponse).session_id ?? null;
                    seenRef.current = [];
                }
                // Page context rides along invisibly so "this page" questions work.
                const pageCtx = capturePageContext();
                const message = `[USER'S CURRENT PAGE — use if the question refers to it, ignore otherwise]\n${pageCtx}\n\n[QUESTION]\n${t}`;
                const r = await fetch(`${searchBaseUrl}v1/agent/send`, {
                    method: "POST",
                    headers: authHeaders,
                    body: JSON.stringify({
                        session_id: sessionRef.current,
                        text: message,
                        seen: seenRef.current,
                    }),
                });
                if (!r.ok) throw new Error(`agent/send ${r.status}`);
                pumpAbortRef.current?.abort();
                const ctrl = new AbortController();
                pumpAbortRef.current = ctrl;
                await pump((await r.json()) as RelayResponse, ctrl.signal);
            } catch (e) {
                if ((e as Error)?.name !== "AbortError") {
                    setError(String((e as Error)?.message || e));
                }
            } finally {
                setBusy(false);
            }
        },
        [searchBaseUrl, getToken, busy, pump, push],
    );
    askRef.current = ask;

    useEffect(() => {
        if (!minimized) {
            threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }, [chat.length, busy, minimized]);

    if (!available) return null;

    if (minimized) {
        return (
            <button
                type="button"
                data-testid="search-agent-launcher"
                onClick={() => setMinimized(false)}
                aria-label="Open the search agent"
                style={{
                    position: "fixed",
                    bottom: 20,
                    right: 20,
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 16px",
                    borderRadius: 999,
                    border: "none",
                    background: "#1f4f6f",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                }}
            >
                <span aria-hidden>✦</span>
                {chat.length ? "Search agent" : "Ask the search agent"}
            </button>
        );
    }

    return (
        <div
            data-testid="search-agent-dock"
            style={{
                position: "fixed",
                bottom: 20,
                right: 20,
                zIndex: 1000,
                width: 400,
                maxHeight: "68vh",
                display: "flex",
                flexDirection: "column",
                borderRadius: 12,
                overflow: "hidden",
                background: "#f8fafc",
                border: "1px solid #d7dde3",
                boxShadow: "0 10px 34px rgba(0,0,0,0.3)",
                fontFamily: "inherit",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "#1f4f6f",
                    color: "#fff",
                }}
            >
                <span aria-hidden>✦</span>
                <strong style={{ fontSize: 14 }}>Search agent</strong>
                <span style={{ fontSize: 10, opacity: 0.8, textTransform: "uppercase" }}>dev</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    <button
                        type="button"
                        aria-label="Minimize agent"
                        onClick={() => setMinimized(true)}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: 16,
                        }}
                    >
                        –
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: 12, minHeight: 120 }}>
                {chat.length === 0 && !busy && (
                    <div
                        style={{
                            color: "#8a94a0",
                            fontSize: 13,
                            textAlign: "center",
                            padding: "20px 8px",
                        }}
                    >
                        Ask about your data or about the page you're on — the agent searches
                        records, follows relationships, and knows where you are.
                    </div>
                )}
                {chat.map((item, i) =>
                    item.role === "user" ? (
                        <div
                            key={i}
                            style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}
                        >
                            <div
                                style={{
                                    maxWidth: "85%",
                                    padding: "8px 12px",
                                    borderRadius: "14px 14px 4px 14px",
                                    background: "#1f4f6f",
                                    color: "#fff",
                                    fontSize: 13,
                                }}
                            >
                                {item.text}
                            </div>
                        </div>
                    ) : item.role === "activity" ? (
                        <div
                            key={i}
                            style={{ fontSize: 11, color: "#8a94a0", margin: "4px 0 4px 4px" }}
                        >
                            ⚙ {item.text}
                        </div>
                    ) : (
                        <div
                            key={i}
                            style={{
                                maxWidth: "95%",
                                padding: "8px 12px",
                                marginBottom: 8,
                                borderRadius: "14px 14px 14px 4px",
                                background: "#fff",
                                border: "1px solid #e3e8ee",
                                fontSize: 13,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {item.text}
                        </div>
                    ),
                )}
                {busy && (
                    <div style={{ fontSize: 13, color: "#8a94a0" }}>Thinking and searching…</div>
                )}
                {error && (
                    <div style={{ fontSize: 12, color: "#c0392b", marginTop: 6 }}>{error}</div>
                )}
                <div ref={threadEndRef} />
            </div>

            <form
                style={{
                    display: "flex",
                    gap: 8,
                    padding: "8px 12px",
                    borderTop: "1px solid #e3e8ee",
                    background: "#fff",
                }}
                onSubmit={(e) => {
                    e.preventDefault();
                    if (draft.trim() && !busy) {
                        void ask(draft);
                        setDraft("");
                    }
                }}
            >
                <input
                    data-testid="search-agent-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={chat.length ? "Ask a follow-up…" : "Ask the agent…"}
                    style={{
                        flex: 1,
                        fontSize: 13,
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: "1px solid #cdd6de",
                        outline: "none",
                    }}
                />
                <button
                    type="submit"
                    disabled={busy || !draft.trim()}
                    aria-label="Send"
                    style={{
                        padding: "6px 12px",
                        borderRadius: 6,
                        border: "none",
                        background: busy || !draft.trim() ? "#9db4c4" : "#1f4f6f",
                        color: "#fff",
                        cursor: busy || !draft.trim() ? "default" : "pointer",
                        fontSize: 13,
                    }}
                >
                    ➤
                </button>
            </form>
        </div>
    );
}
